<template>
  <div class="form-card">
    <div class="form-card__inner">
      <div class="form-card__header">
        <div class="form-card__icon">
          <img
            src="@/assets/icons/card.svg"
            alt="phone"
          >
        </div>
        <h3 class="form-card__title">
          {{$t('bank_transfer.title')}}
        </h3>
        <div class="form-card__subtitle">
          {{$t('bank_transfer.subtitle')}}
        </div>
      </div>
      <div class="form-card__input">
        <ap-input-tel
          v-model="sender.phone"
          :label="$t('from')"
          :icon-name="icon"
          :error-text="errors?.phone?.[0] || ''"
          @keyup="checkOperator"
        />
      </div>
      <div class="form-card__input">
        <ap-input-mask  
          v-model="sender.card_number"
          type="cardNumber"
          :label="$t('bank_transfer.to')"
          :placeholder="$t('bank_transfer.card_placeholder')"
          :error-text="errors?.card_number?.[0] || ''"
          full
          @keyup="onKeyup"
        />
      </div>
      <div class="form-card__confirmation">
        <div class="confirmation">
          <ap-input-checkbox 
            v-model:checked="sender.confirmation"
            class="confirmation__input"
            @keyup="onKeyup"
          />
          <div class="confirmation__text">{{$t('bank_transfer.confirm')}}</div>
        </div>
      </div>
      <div class="form-card__input form-card__input--last">
        <ap-input-money
          v-model="sender.amount" 
          :error-text="errors?.amount?.[0] || ''"
          :label="$t('bank_transfer.sum_label')"
          currency
          :hint="hintText"
          @keyup="onKeyup"
        />
      </div>
      <div class="form-card__total total total--form-card">
        <div class="total__item">
          <div class="total__text">
            {{ remittancesToCard?.commission?.text  || $t('bank_transfer.hint') }} 
          </div>
          <div class="total__number">
            {{ remittancesToCard?.commission?.amount  || 0 }} ₸
          </div>
        </div>
        <div class="total__item">
          <div class="total__text">
            {{$t('write_off_amount')}}
          </div>
          <div class="total__number">
            {{ remittancesToCard?.commission?.total || 0 }} ₸
          </div>
        </div>
      </div>
      <div class="form-card__btn">
        <ap-button
          type="primary"
          full
          :disable="btnIsDisable"
          @click="pay"
        >
          <div class="button__loader">
            <div
              v-if="checking"
              class="spinner"
            />
            <div v-else>
              {{$t('continue')}}
            </div>
          </div>
        </ap-button>
      </div>
      <div class="form-card__error">
        {{ errors?.message }}
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import debounce from 'lodash/debounce';

import {
  PHONE_LENGTH,
  REMITTAMCES_TO_CARD,
  STATUS_OK
} from '@/constant';

import { BankTransfer } from '@/util/interface';

import apButton from './UI/apButton.vue';
import apInput from './UI/apInput.vue';
import ApInputMask from './UI/apInputMask.vue';
import ApInputMoney from './UI/apInputMoney.vue';
import apInputTel from './UI/apInputTel.vue';
import ApInputCheckbox from './UI/apCheckbox2.vue';
import { luhnCheck } from '@/util/helper';

export default defineComponent({
  components: {
    apInputTel,
    apInput,
    apButton,
    ApInputMask,
    ApInputMoney,
    ApInputCheckbox
  },

  setup() {
    const store = useStore();
    const router = useRouter();
    const { t } = useI18n();

    const offerChecked = ref(true);
    
    const sender = ref<BankTransfer>({
      phone: '',
      amount: '',
      card_number: '',
      confirmation: true,

    });

    const checking = ref(false);

    const btnText = ref('Продолжить');
    
    const btnIsDisable = ref(true);
    
    const errors = computed(() => store.getters['cardRemittances/remittancesToCardErrors']);

    const remittancesToCard = computed(() => store.getters['cardRemittances/remittancesToCard']);
    
    const language = computed(() => store.getters['language']);

    const defaultMerchant = computed(() => store.getters['isDefault']);

    const subtitle = computed(() => defaultMerchant.value ? ' Перевести с баланса на Visa, MasterCard, Humo или Uzcard': ' Перевести с баланса на Visa, MasterCard');

    const hintText = computed(() =>  ''); 

    const icon = computed(() => {
      const data = store.getters['bankTransferOperator'];
      const { operator } = data;

      if (operator) {       
        return operator;
      }
      return '';
    });

    const clearErrors = (): void => {
      if (errors.value) {
        store.commit('cardRemittances/setRemittancesToCardErrors', {});
      }
    };

    const getTransferInfo = async (): Promise<void> => {
      btnIsDisable.value = true;
      
      const isFormFill = sender.value.phone.length === PHONE_LENGTH 
         && sender.value.card_number?.length === 16 
         && +sender.value.amount > 0 
         && sender.value.confirmation;

      if (isFormFill){
        const luhn = luhnCheck(sender.value.card_number);
        if (!luhn) {
          store.commit('cardRemittances/setRemittancesToCardErrors', {card_number: ['Номер карты недействителен']});
          btnIsDisable.value = true;
          return;
        }

        await store.dispatch('cardRemittances/prevalidationRemittancesToCard', {
          amount: parseInt(sender.value.amount),
          phone: `7${sender.value.phone}`,
          card_number: sender.value.card_number,
          language: language.value
        }).then((res) => {
          if (res.data && res.data.errors) {
            btnIsDisable.value = true;
            return;
          }
          btnIsDisable.value = false;
        }).catch(() => {
          btnIsDisable.value = true;
        });
      } else {
        btnIsDisable.value = true;
        clearErrors();
        store.commit('cardRemittances/setRemittancesToCard', {});
      }
    };  

    const checkOperator = async (): Promise<void> => {
      sender.value.phone.length === PHONE_LENGTH
        ? await store.dispatch('checkOperator', { phone: `7${sender.value.phone}`,
          type: 'card' })
        : store.commit('setOperator', {type: 'card'});
      
      await getTransferInfo();
      clearErrors();
    };

    const onKeyup = debounce(function () { 
      getTransferInfo();
    } , 1000);
    

    const pay = async ():Promise<void> => {
      checking.value = true;
      
      const data = {
        amount: parseInt(sender.value.amount),
        phone: `7${sender.value.phone}`,
        card_number: sender.value.card_number,
        language: language.value
      };
      
      const status = await store.dispatch('cardRemittances/prevalidationRemittancesToCard', data);
      
      if (status?.status === STATUS_OK) {
        const otp = await store.dispatch('cardRemittances/initializationCardRemittances', data);
        if (otp && otp.confirm_inputs) {
          const data = {
            id: otp.id,
            language: language.value
          };
          await store.dispatch('cardRemittances/resendOtp', data);

          store.commit('setOtp', {
            phone: sender.value.phone,
            service_id:  otp.id,
            paymentType: REMITTAMCES_TO_CARD,
            otp: ''
          });

          router.push({ name: 'confirm',
            params: { 
              phone: sender.value.phone,
              service_id: otp.id,
              paymentType: REMITTAMCES_TO_CARD,
              otp: ''
            } 
          });
          return;
        }

        if (otp && otp.data.errors) {
          checking.value = false;
          return;
        }

        router.push({ name: 'service-status',
          params: { phone: sender.value.phone,
            service_id: otp.id,
            paymentType: REMITTAMCES_TO_CARD } });

        return;
      }
      checking.value = false;
    };
    
    onBeforeMount(() => {
      store.commit('cardRemittances/setRemittancesToCardErrors', {});
      store.commit('setLoading', false);
    });

    return {
      sender,
      pay,
      btnIsDisable,
      icon,
      onKeyup,
      errors,
      btnText,
      checking,
      subtitle,
      clearErrors,
      checkOperator,
      hintText,
      offerChecked,                      
      getTransferInfo,
      remittancesToCard,
      t
    };
  },
});
</script>
<style scoped lang="scss">
.form-card{
  background-color: $primary-white;
  border-radius: 24px;
}

 .confirmation{
   display: flex;
   &__input{
     margin-right: 12px;
   }
   &__text{
    color: rgba(14, 36, 58, 0.5);
   }
 }
.default{
  .form-card{
    padding: 34px 40px 40px 40px;
    &__icon{
      margin-bottom: 7px;
    }
    &__title{
      font-style: normal;
      font-weight: 700;
      font-size: 32px;
      line-height: 38px;
      margin-bottom: 8px;
    }
    &__subtitle{
      font-weight: 400;
      font-size: 16px;
      line-height: 140%;
    }
    &__header{
      margin-bottom: 38px;
    }
    &__input:first-child{
      margin-bottom: 22px;
    }
    &__input{
      margin-bottom: 24px;
    }
    &__total{
      margin-bottom: 20px;
    }
    &__confirmation{
      margin-bottom: 32px;
    }
  }
}
.tele2, .altel{
  
  .form-card{
    padding: 40px;
    background-color: #ffffff;
    &__inner{
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
    &__icon{
      display: none;
    }
    &__title{
      font-weight: bold;
    }
    &__subtitle{
      color: $primary-gray;
    }
    &__input:first-child {
      margin-bottom: 24px;
    }
    &__input {
      margin-bottom: 40px;
    }
    &__input--last {
      margin-bottom: 20px;
    }
    &__total{
      margin-bottom: 20px;
    }
    &__btn{
      margin: 0;
    }
    &__confirmation{
      margin-bottom: 32px;
    }
  }
}
.tele2{
  .form-card{
    &__title{
      font-style: normal;
      font-weight: 700;
      font-size: 32px;
      line-height: 38px;
      margin-bottom: 8px;
      color: $tele2-primary-black;
    }
    &__subtitle{
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
      font-family: 'Open Sans';
      color: $tele2-primary-gray;
    }
    &__header{
      margin-bottom: 24px;
    }
  }
}
.altel{
  .form-card{
    padding: 40px;
    &__title{
      font-weight: 700;
      font-size: 40px;
      line-height: 48px;
      margin-bottom: 6px;
      color: $altel-primary-black;
    }
    &__subtitle{
      font-weight: 500;
      font-size: 18px;
      line-height: 22px;
      color: $altel-primary-gray;

    }
    &__header{
      margin-bottom: 28px;
    }
  }  
}
@media (min-width: 320px) and (max-width: 1024px) {
  .tele2, .altel, .default{
    .form-card{
      &__title{
        font-weight: 700;
        font-size: 24px;
        line-height: 34px;
      }
    }
  }
  .default{
    .form-card{
      padding: 21px 16px 16px 16px;
    }
  }
  .tele2, .altel{
    .form-card{
      padding: 40px 16px;
    }
  }
}
</style>
